import { routerApolloClient } from '@/shared/graphql/client'
import { DnaCaptureMode } from '@/shared/graphql/router/graphql'

import {
  GENERIC_MESSAGE,
  isCapacityError,
  readDetectorError,
  sleep,
} from './errors'
import {
  mergeFindings,
  readDetection,
  type DetectionResult,
  type Finding,
} from './findings'
import {
  DETECT_DNA_CAPTURE_BATCH_MUTATION,
  START_DNA_CAPTURE_MUTATION,
  STOP_DNA_CAPTURE_MUTATION,
} from './operations'
import {
  cameraPacket,
  PcmBatcher,
  toBase64,
  wavFile,
  waveformPeaks,
} from './pcm'
import type { CheckerResults } from './results'

export const SAMPLE_RATE = 48000
const ADMISSION_WAIT_MS = 180_000
const DRAIN_MS = 35_000
const BATCH_ATTEMPTS = 3
const WAVEFORM_BUCKETS = 120
const MAX_FRAMES = 12
const MAX_DIMENSION = 960
const MAX_JPEG_BYTES = 256 * 1024

const WORKLET = `
class DacrCapture extends AudioWorkletProcessor {
  constructor() {
    super()
    this.batch = new Int16Array(sampleRate)
    this.batchUsed = 0
    this.meter = new Int16Array(2048)
    this.meterUsed = 0
    this.stopped = false
    this.port.onmessage = (event) => {
      if (event.data !== 'flush' || this.stopped) return
      this.stopped = true
      if (this.batchUsed) this.emitBatch()
      this.port.postMessage('flushed')
    }
  }
  encode(samples, used) {
    const buffer = new ArrayBuffer(used * 2)
    const view = new DataView(buffer)
    for (let j = 0; j < used; j++) view.setInt16(j * 2, samples[j], true)
    return buffer
  }
  emitBatch() {
    const buffer = this.encode(this.batch, this.batchUsed)
    this.port.postMessage({ batch: buffer }, [buffer])
    this.batchUsed = 0
  }
  emitMeter() {
    const buffer = this.encode(this.meter, this.meterUsed)
    this.port.postMessage({ meter: buffer }, [buffer])
    this.meterUsed = 0
  }
  process(inputs) {
    if (this.stopped) return true
    const channels = inputs[0]
    if (!channels || !channels.length) return true
    for (let i = 0; i < channels[0].length; i++) {
      let value = 0
      for (const channel of channels) value += channel[i] / channels.length
      const clamped = Math.max(-1, Math.min(1, value))
      const sample = Math.round(clamped * (clamped < 0 ? 32768 : 32767))
      this.batch[this.batchUsed++] = sample
      this.meter[this.meterUsed++] = sample
      if (this.batchUsed === this.batch.length) this.emitBatch()
      if (this.meterUsed === this.meter.length) this.emitMeter()
    }
    return true
  }
}
registerProcessor('dacr-capture', DacrCapture)
`

export type LivePhase =
  | 'idle'
  | 'starting'
  | 'waiting'
  | 'recording'
  | 'draining'
  | 'finished'
  | 'failed'

export interface LiveSnapshot {
  phase: LivePhase
  findings: ReadonlyMap<string, Finding>
  error: string | null
  microphoneBusy: boolean
  results: CheckerResults | null
}

interface Pending {
  payload: string | null
}

interface Session {
  id: string
  maxAudioSeconds: number
}

const idle: LiveSnapshot = {
  phase: 'idle',
  findings: new Map(),
  error: null,
  microphoneBusy: false,
  results: null,
}

export async function captureFrame(
  video: HTMLVideoElement
): Promise<{ bytes: Uint8Array; blob: Blob; url: string }> {
  if (!video.videoWidth || !video.videoHeight)
    throw new Error('The camera frame is not ready.')
  const scale = Math.min(
    1,
    MAX_DIMENSION / Math.max(video.videoWidth, video.videoHeight)
  )
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.round(video.videoWidth * scale))
  canvas.height = Math.max(1, Math.round(video.videoHeight * scale))
  canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height)
  for (const quality of [0.85, 0.6]) {
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', quality)
    )
    if (!blob || blob.type !== 'image/jpeg')
      throw new Error('Camera capture failed.')
    if (blob.size <= MAX_JPEG_BYTES)
      return {
        bytes: new Uint8Array(await blob.arrayBuffer()),
        blob,
        url: URL.createObjectURL(blob),
      }
  }
  throw new Error('The camera frame is too large to check.')
}

export class LiveCheck {
  private snapshot: LiveSnapshot = idle
  private session: Session | null = null
  private context: AudioContext | null = null
  private node: AudioWorkletNode | null = null
  private source: MediaStreamAudioSourceNode | null = null
  private batcher = new PcmBatcher(SAMPLE_RATE, (pcm) => this.enqueue(pcm))
  private pending: Pending[] = []
  private inflight = false
  private sequence = 0
  private pcm: Uint8Array[] = []
  private frames: string[] = []
  private lastFrame: Blob | null = null
  private matchIds = new Set<string>()
  private recordedAt: number | undefined
  private frameChain: Promise<unknown> = Promise.resolve()
  private limitTimer: ReturnType<typeof setTimeout> | null = null
  private drainTimer: ReturnType<typeof setTimeout> | null = null
  private flushed = false
  private awaitingFlush = false
  private stopped = false
  private disposed = false

  constructor(
    private readonly mode: DnaCaptureMode,
    private readonly stream: MediaStream,
    private readonly onChange: (snapshot: LiveSnapshot) => void,
    private readonly onPcm?: (pcm: Uint8Array, sampleRate: number) => void,
    private readonly video?: HTMLVideoElement
  ) {}

  private update(patch: Partial<LiveSnapshot>) {
    if (this.disposed) return
    this.snapshot = { ...this.snapshot, ...patch }
    this.onChange(this.snapshot)
  }

  async start() {
    if (this.snapshot.phase !== 'idle') return
    this.update({ phase: 'starting' })
    try {
      const session = await this.admit()
      if (!session) return
      this.session = session
      if (this.stopped) {
        void this.closeSession().catch(() => {})
        return
      }
      await this.openAudio()
      if (this.stopped) return
      this.recordedAt = Date.now()
      this.update({ phase: 'recording' })
      this.limitTimer = setTimeout(
        () => void this.finish(),
        session.maxAudioSeconds * 1000
      )
    } catch (error) {
      this.fail(error instanceof Error ? error.message : GENERIC_MESSAGE)
    }
  }

  private async openAudio() {
    let context: AudioContext
    try {
      context = new AudioContext({ sampleRate: SAMPLE_RATE })
    } catch {
      throw new Error('This browser can’t record audio at the required rate.')
    }
    this.context = context
    if (context.sampleRate !== SAMPLE_RATE)
      throw new Error('This browser can’t record audio at the required rate.')
    await context.resume()
    const url = URL.createObjectURL(
      new Blob([WORKLET], { type: 'application/javascript' })
    )
    try {
      await context.audioWorklet.addModule(url)
    } finally {
      URL.revokeObjectURL(url)
    }
    const source = context.createMediaStreamSource(this.stream)
    const node = new AudioWorkletNode(context, 'dacr-capture')
    node.port.onmessage = (
      event: MessageEvent<
        { batch: ArrayBuffer } | { meter: ArrayBuffer } | string
      >
    ) => {
      const { data } = event
      if (data === 'flushed') {
        this.flushed = true
        this.releaseAudio()
        this.pump()
        return
      }
      if (typeof data === 'string') return
      if (this.snapshot.phase !== 'recording' && !this.awaitingFlush) return
      if ('meter' in data) {
        this.onPcm?.(new Uint8Array(data.meter), SAMPLE_RATE)
        return
      }
      this.batcher.push(data.batch)
    }
    source.connect(node)
    node.connect(context.destination)
    this.source = source
    this.node = node
  }

  private async admit(): Promise<Session | null> {
    const deadline = Date.now() + ADMISSION_WAIT_MS
    for (;;) {
      try {
        const { data } = await routerApolloClient.mutate({
          mutation: START_DNA_CAPTURE_MUTATION,
          variables: { input: { mode: this.mode, sampleRate: SAMPLE_RATE } },
          fetchPolicy: 'no-cache',
        })
        if (!data) throw new Error(GENERIC_MESSAGE)
        return data.startDnaCapture
      } catch (error) {
        const detector = readDetectorError(error)
        const wait = Math.max(1, detector.retryAfterSeconds ?? 1) * 1000
        if (this.stopped) return null
        if (!isCapacityError(detector) || Date.now() + wait > deadline)
          throw new Error(detector.message)
        this.update({ phase: 'waiting' })
        await sleep(wait + Math.floor(Math.random() * 250))
        if (this.stopped) return null
      }
    }
  }

  private enqueue(pcm: Uint8Array) {
    this.pcm.push(pcm)
    if (this.stopped) return
    const entry: Pending = { payload: null }
    this.pending.push(entry)
    const video = this.video
    if (this.mode !== DnaCaptureMode.Camera || !video) {
      entry.payload = toBase64(pcm)
      this.pump()
      return
    }
    this.frameChain = this.frameChain
      .then(() => captureFrame(video))
      .then(({ bytes, blob, url }) => {
        if (this.stopped) {
          URL.revokeObjectURL(url)
          return
        }
        entry.payload = toBase64(cameraPacket(bytes, pcm))
        this.lastFrame = blob
        this.frames.push(url)
        if (this.frames.length > MAX_FRAMES) {
          const dropped = this.frames.shift()
          if (dropped) URL.revokeObjectURL(dropped)
        }
        this.pump()
      })
      .catch((error: unknown) =>
        this.fail(
          error instanceof Error ? error.message : 'Camera capture failed.'
        )
      )
  }

  private pump() {
    if (this.stopped || this.inflight || !this.session) return
    const head = this.pending[0]
    if (!head?.payload) {
      if (!head) this.maybeComplete()
      return
    }
    this.pending.shift()
    this.inflight = true
    this.sequence += 1
    void this.send(this.session.id, this.sequence, head.payload)
  }

  private async send(id: string, sequence: number, data: string) {
    for (let attempt = 1; ; attempt++) {
      try {
        const response = await routerApolloClient.mutate({
          mutation: DETECT_DNA_CAPTURE_BATCH_MUTATION,
          variables: { input: { id, sequence, data } },
          fetchPolicy: 'no-cache',
        })
        const result = readDetection(response.data?.detectDnaCaptureBatch)
        if (this.stopped) return
        result?.infringementMatchIds?.forEach((match) =>
          this.matchIds.add(match)
        )
        if (result)
          this.update({
            findings: mergeFindings(this.snapshot.findings, result.findings),
          })
        break
      } catch (error) {
        if (this.stopped) return
        const detector = readDetectorError(error)
        if (detector.code === 'MEDIA_TOO_LONG') {
          this.review()
          return
        }
        if (!detector.retryable || attempt >= BATCH_ATTEMPTS) {
          this.fail(detector.message)
          return
        }
        await sleep(
          Math.max(0.5, detector.retryAfterSeconds ?? attempt * 0.5) * 1000
        )
        if (this.stopped) return
      }
    }
    this.inflight = false
    this.pump()
  }

  async finish() {
    const { phase } = this.snapshot
    if (phase === 'starting' || phase === 'waiting') {
      this.dispose()
      this.update({ ...idle })
      return
    }
    if (phase !== 'recording') return
    this.clearLimit()
    this.update({ phase: 'draining' })
    this.drainTimer = setTimeout(() => void this.complete(), DRAIN_MS)
    if (this.node) {
      this.awaitingFlush = true
      this.node.port.postMessage('flush')
      return
    }
    this.flushed = true
    this.pump()
  }

  review() {
    const { phase } = this.snapshot
    if (phase !== 'recording' && phase !== 'draining') return
    this.update({ phase: 'draining' })
    void this.complete()
  }

  private releaseAudio() {
    this.awaitingFlush = false
    this.batcher.flush()
    this.node?.disconnect()
    this.source?.disconnect()
    this.node = null
    this.source = null
    void this.context?.close().catch(() => {})
    this.context = null
  }

  private maybeComplete() {
    if (
      this.snapshot.phase === 'draining' &&
      this.flushed &&
      !this.inflight &&
      !this.pending.length
    )
      void this.complete()
  }

  private async complete() {
    if (this.snapshot.phase !== 'draining' || this.stopped) return
    this.stopped = true
    this.clearLimit()
    if (this.drainTimer) clearTimeout(this.drainTimer)
    this.releaseAudio()
    this.pending = []
    try {
      const result = await this.closeSession()
      if (this.disposed) return
      if (result) {
        result.infringementMatchIds?.forEach((match) =>
          this.matchIds.add(match)
        )
        this.update({
          findings: mergeFindings(this.snapshot.findings, result.findings),
        })
      }
      this.update({ phase: 'finished', results: this.assemble() })
    } catch (error) {
      if (this.disposed) return
      this.update({ phase: 'failed', error: readDetectorError(error).message })
    }
  }

  private assemble(): CheckerResults {
    const findings = [...this.snapshot.findings.values()]
    const bytes = this.pcm.reduce((sum, chunk) => sum + chunk.length, 0)
    const recording = {
      recordedAt: this.recordedAt,
      durationSeconds: bytes / (SAMPLE_RATE * 2),
      matchIds: [...this.matchIds],
    }
    if (this.mode === DnaCaptureMode.Camera)
      return {
        source: 'visual',
        findings,
        media: this.lastFrame
          ? new File([this.lastFrame], `Captured visual ${stamp()}.jpg`, {
              type: 'image/jpeg',
            })
          : null,
        peaks: [],
        frames: [...this.frames],
        ...recording,
      }
    return {
      source: 'audio',
      findings,
      media: new File(
        [wavFile(this.pcm, SAMPLE_RATE)],
        `Recorded audio ${stamp()}.wav`,
        { type: 'audio/wav' }
      ),
      peaks: waveformPeaks(this.pcm, WAVEFORM_BUCKETS),
      frames: [],
      ...recording,
    }
  }

  private fail(message: string, microphoneBusy = false) {
    if (this.stopped) return
    this.dispose()
    this.update({ phase: 'failed', error: message, microphoneBusy })
  }

  private clearLimit() {
    if (this.limitTimer) clearTimeout(this.limitTimer)
    this.limitTimer = null
  }

  private async closeSession(): Promise<DetectionResult | null> {
    const session = this.session
    this.session = null
    if (!session) return null
    const { data } = await routerApolloClient.mutate({
      mutation: STOP_DNA_CAPTURE_MUTATION,
      variables: { id: session.id },
      fetchPolicy: 'no-cache',
    })
    if (!data?.stopDnaCapture.success) throw new Error(GENERIC_MESSAGE)
    return readDetection(data.stopDnaCapture.result)
  }

  dispose() {
    this.stopped = true
    this.clearLimit()
    if (this.drainTimer) clearTimeout(this.drainTimer)
    this.releaseAudio()
    void this.closeSession().catch(() => {})
    this.pending = []
  }

  destroy() {
    this.dispose()
    this.disposed = true
  }
}

const stamp = () => {
  const now = new Date()
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}.${pad(now.getMinutes())}`
}
